import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { createSelfScreen, listScreens } from './api/screens.js';
import { siteOrigin } from './invite.js';
import { isNativeShell } from './platform.js';

/**
 * Tela grande pelo próprio celular (Android): o seletor de TV do sistema (Smart View,
 * Transmitir) liga a TV como tela secundária, e o app desenha nela a página da tela,
 * já pareada, sem código. Fora do app instalado nada aqui faz efeito.
 */
type ScreenPlugin = {
  status(): Promise<CastStatus>;
  openChooser(): Promise<{ opened: boolean; via?: string }>;
  present(options: { url: string }): Promise<{ shown: boolean; name?: string; reason?: string }>;
  dismiss(): Promise<void>;
  addListener(
    event: 'screenDisplay',
    listener: (status: CastStatus) => void,
  ): Promise<PluginListenerHandle>;
};
export type CastStatus = {
  supported: boolean;
  displays: number;
  name: string | null;
  showing: boolean;
};

const plugin = registerPlugin<ScreenPlugin>('SecretarioScreen');

export function castSupported(): boolean {
  return isNativeShell() && Capacitor.getPlatform() === 'android';
}

async function quiet<T>(call: () => Promise<T>): Promise<T | null> {
  if (!castSupported()) return null;
  try {
    return await call();
  } catch {
    return null;
  }
}

export const castStatus = () => quiet(() => plugin.status());
export const openCastChooser = () => quiet(() => plugin.openChooser());
export const dismissTv = () => quiet(() => plugin.dismiss());

export function watchCastDisplays(onChange: (status: CastStatus) => void): () => void {
  if (!castSupported()) return () => {};
  let handle: PluginListenerHandle | null = null;
  void plugin
    .addListener('screenDisplay', onChange)
    .then((h) => {
      handle = h;
    })
    .catch(() => {});
  return () => void handle?.remove();
}

/**
 * A URL pública da página da tela, com o segredo da tela recém-criada. No app instalado a
 * origem da página é `https://localhost` (o próprio aparelho): a TV abria "localhost" e
 * recusava a conexão. Por isso o endereço público do Secretário, nunca o da página.
 */
function telaUrl(token: string): string {
  // `via=celular`: a página sabe que está na TV pelo celular e avisa quando a exibição acaba.
  return `${siteOrigin()}/tela?token=${encodeURIComponent(token)}&via=celular`;
}

export type TvStep = 'buscando' | 'escolher' | 'encontrada' | 'conectando' | 'mostrando' | 'falhou';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * "Conecta na TV da sala": procura uma TV já ligada ao celular; sem nenhuma, abre o
 * seletor do Android e espera a pessoa escolher; com a TV, cria a tela pareada no
 * servidor e desenha a página nela; por fim espera a tela se apresentar ao servidor.
 * Cada etapa chega em `onStep` para a conversa mostrar o raciocínio.
 */
export async function connectTv(options: {
  name?: string;
  onStep: (step: TvStep, detail?: string) => void;
  /** Quanto esperar a pessoa escolher a TV no seletor (ms). */
  chooseTimeoutMs?: number;
}): Promise<{ ok: boolean; name: string; reason?: string }> {
  // Espelhamento comum ("Transmitir tela inteira") não cria tela secundária: sem ela em
  // 40 s, desiste e explica, em vez de prender a conversa por minuto e meio.
  const chooseTimeout = options.chooseTimeoutMs ?? 40_000;
  if (!castSupported()) return { ok: false, name: '', reason: 'sem_suporte' };
  options.onStep('buscando');
  let status = await castStatus();
  if (!status) return { ok: false, name: '', reason: 'sem_suporte' };
  if (status.displays === 0) {
    options.onStep('escolher');
    const opened = await openCastChooser();
    if (!opened?.opened) return { ok: false, name: '', reason: 'sem_seletor' };
    const started = Date.now();
    while (Date.now() - started < chooseTimeout) {
      await wait(1200);
      status = await castStatus();
      if (status && status.displays > 0) break;
    }
    if (!status || status.displays === 0) return { ok: false, name: '', reason: 'nao_escolheu' };
  }
  const tvName = (options.name?.trim() || status.name || 'TV').slice(0, 60);
  options.onStep('encontrada', tvName);
  let token = '';
  let screenId = '';
  try {
    const created = await createSelfScreen(tvName);
    token = created.token;
    screenId = created.screen.id;
  } catch {
    return { ok: false, name: tvName, reason: 'servidor' };
  }
  options.onStep('conectando', tvName);
  const shown = await quiet(() => plugin.present({ url: telaUrl(token) }));
  if (!shown?.shown) return { ok: false, name: tvName, reason: shown?.reason ?? 'sem_tela' };
  // A página na TV abre e se apresenta ao servidor (SSE); aí a conexão está de pé.
  for (let tries = 0; tries < 20; tries += 1) {
    await wait(900);
    try {
      const screens = await listScreens();
      if (screens.find((item) => item.id === screenId)?.connected) {
        options.onStep('mostrando', tvName);
        return { ok: true, name: tvName };
      }
    } catch {
      /* rede piscou: tenta de novo */
    }
  }
  // A TV recebeu a página mas o servidor ainda não a viu: quase sempre só atraso.
  options.onStep('mostrando', tvName);
  return { ok: true, name: tvName };
}

/** Texto de cada etapa, na voz do Secretário. */
export function tvStepLabel(step: TvStep, detail?: string): string {
  switch (step) {
    case 'buscando':
      return 'Buscando a TV';
    case 'escolher':
      return 'Escolha a TV na tela do celular';
    case 'encontrada':
      return `${detail ?? 'TV'} encontrada`;
    case 'conectando':
      return `Conectando à ${detail ?? 'TV'}`;
    case 'mostrando':
      return `Mostrando na ${detail ?? 'TV'}`;
    case 'falhou':
      return detail ?? 'Não consegui conectar';
  }
}
